import { useRef, type RefObject } from 'react';
import { Spinner } from '@/shared/ui';
import type { JobProgress, ProcessingStatus } from '@/shared/types';
import { PlayerControls } from '@/features/content/components/PlayerControls';
import { JobProgressChecklist } from '@/features/generation/components/JobProgressChecklist';

export type ContentStageProps = {
  videoRef: RefObject<HTMLVideoElement | null>;
  videoUrl?: string;
  thumbnailUrl?: string;
  /* Optional because `VideoDetail.status` is: a record can exist before the
     backend has classified it. An unknown status falls through to the rendering
     placeholder, which is the honest answer — "we don't know yet". */
  status?: ProcessingStatus;
  /* Live stage counts while the job renders. Absent for a queued job that has no
     plan yet, and for any video opened after the session that generated it. */
  progress?: JobProgress;
  /** Blob URL for the WebVTT track, or null when there is nothing to show. */
  captionsUrl: string | null;
  onTimeUpdate: () => void;
  /** Fires once the element knows its duration — where a resume seek belongs. */
  onLoadedMetadata?: () => void;
  theater: boolean;
  onToggleTheater: () => void;
};

/**
 * The player, or what stands in for it.
 *
 * Four states, and each is a different thing to tell the user: the video plays;
 * it failed; it was cancelled; or it is still rendering, where the thumbnail is
 * shown behind a spinner so the wait has something in it.
 */
export function ContentStage({
  videoRef,
  videoUrl,
  thumbnailUrl,
  status,
  progress,
  captionsUrl,
  onTimeUpdate,
  onLoadedMetadata,
  theater,
  onToggleTheater,
}: ContentStageProps) {
  const stageRef = useRef<HTMLDivElement | null>(null);

  if (videoUrl) {
    return (
      <div className="content-page__stage" ref={stageRef}>
        <video
          ref={videoRef}
          src={videoUrl}
          // Without a poster the player is a black rectangle until the user
          // presses play — the browser doesn't decode a first frame on its own.
          poster={thumbnailUrl}
          /* No `controls`: PlayerControls draws the bar the frames specify.
             Clicking the picture still toggles playback, which is the one
             native behaviour worth keeping by hand. */
          className="content-page__video"
          onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={onLoadedMetadata}
          onClick={() => {
            const video = videoRef.current;
            if (!video) return;
            if (video.paused) void video.play().catch(() => undefined);
            else video.pause();
          }}
        >
          {captionsUrl ? <track kind="subtitles" srcLang="en" label="English" src={captionsUrl} /> : null}
        </video>

        <PlayerControls
          videoRef={videoRef}
          stageRef={stageRef}
          hasCaptions={captionsUrl !== null}
          theater={theater}
          onToggleTheater={onToggleTheater}
        />
      </div>
    );
  }

  return (
    <div className="content-page__stage">
      <div className="content-page__placeholder">
        {status === 'failed' ? (
          <p>Unable to load this video.</p>
        ) : status === 'cancelled' ? (
          <p>This generation was cancelled.</p>
        ) : (
          <>
            {thumbnailUrl ? <img src={thumbnailUrl} alt="" className="content-page__poster" /> : null}
            {/* The four-stage breakdown when the provider is tracking this job,
                and the spinner when it is not. The spinner alone was all this
                screen ever showed, which told the user less about a render in
                flight than the page it replaced — the checklist has existed and
                been tested the whole time, with nowhere to render. */}
            <div className="content-page__rendering">
              {progress ? (
                <JobProgressChecklist progress={progress} />
              ) : (
                <>
                  <Spinner size={24} label="Rendering" />
                  <p>Valoi Engine is rendering your video…</p>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
